import React, { useEffect, useState, ChangeEvent, useRef } from 'react'
import { FormikProps, withFormik, Form } from 'formik'
import Input from '../../input'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from '../../../helpers/hooks/hookWindowsSize'
import Select from '../../select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import Tooltip from '@mui/material/Tooltip'
import QRCode from 'qrcode.react'
import { ChromePicker } from 'react-color'
import { linkPrimary, qrCodesActiveSecondary } from '../../../images'
import { toast } from 'react-toastify'
import { t } from 'i18next'

type qrValues = {
  color: string
  logo: string
}

type utmValues = {
  origin: string
  media: string
  campaign: string
  term: string
  content: string
}

interface CreateNewFormProps {
  url: string
  domain: string
  title: string
  custom_code: string
  tags: string[]
  has_qr: boolean
  utm: utmValues
  qr_code: qrValues
  qr_code_checkBox: any
  utm_checkBox: any
  onSubmit: (values: CreateNewFormValues) => void
  children?: React.ReactNode
  shortUrlGenerated: string
  domains: string[]
  userInfo: any
  canCreateQRCode: (value: boolean) => void
}

interface CreateNewFormValues {
  url: string
  domain: string
  title: string
  custom_code: string
  tags: string[]
  has_qr: boolean
  utm: utmValues
  qr_code: qrValues
  qr_code_checkBox: any
  utm_checkBox: any
}

const InnerForm: React.FunctionComponent<
  CreateNewFormProps & FormikProps<CreateNewFormValues>
> = ({
  values,
  handleChange,
  touched,
  shortUrlGenerated,
  canCreateQRCode,
  domains,
  userInfo,
}) => {
  const { t } = useTranslation()
  const sizeWidth = useWindowSize().width
  const [isCheckedUTM, setIsCheckedUTM] = useState(false)
  const [isCheckedQR, setIsCheckedQR] = useState(values.qr_code_checkBox)
  const [utmTags, setUtmTags] = useState('-')
  const [url, setUrl] = useState('')
  const [color, setColor] = useState('#000000')
  const [showPaletteColor, setShowPaletteColor] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [customDomain, setCustomDomain] = useState(false)

  const domainData = domains?.map((item) => {
    return { value: item, label: item }
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleCheckboxUTM = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e)
    setIsCheckedUTM(e.target.checked)
  }

  const handleCheckboxQR = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHasQRValue = e.target.checked
    handleChange(e)
    handleChange({
      target: {
        name: 'has_qr',
        value: newHasQRValue,
      },
    })

    if (newHasQRValue && !values.url) {
      toast.error(`${t('ENTER_URL_BEFORE_ACTIVATING_QR_CODE')}`)
      setIsCheckedQR(false)
      handleChange({
        target: {
          name: 'qr_code_checkBox',
          value: false,
        },
      })
    } else {
      setIsCheckedQR(newHasQRValue)
      handleChange({
        target: {
          name: 'qr_code_checkBox',
          value: true,
        },
      })
    }

    if (
      newHasQRValue &&
      (!userInfo?.hasQrCredits || !userInfo?.permissions.has_qr)
    ) {
      canCreateQRCode(false)
      setIsCheckedQR(false)
      handleChange({
        target: {
          name: 'qr_code_checkBox',
          value: false,
        },
      })
    }
  }

  useEffect(() => {
    if (values.domain === domainData[0].value) {
      setCustomDomain(false)
    } else if (
      userInfo.permissions.custom_domain &&
      userInfo.permissions.custom_post_domain &&
      values.domain !== domainData[0].value
    ) {
      setCustomDomain(true)
    }
  }, [values.domain])

  useEffect(() => {
    if (isCheckedQR) values.qr_code.color = color
  }, [color])

  useEffect(() => {
    if (isCheckedQR) values.qr_code_checkBox = true
    else values.qr_code_checkBox = false
  }, [isCheckedQR])

  useEffect(() => {
    if (
      values.utm.origin ||
      values.utm.media ||
      values.utm.campaign ||
      values.utm.term ||
      values.utm.content
    ) {
      const utmParams = []

      if (values.utm.origin) {
        utmParams.push(`utm_source=${values.utm.origin}`)
      }
      if (values.utm.media) {
        utmParams.push(`utm_medium=${values.utm.media}`)
      }
      if (values.utm.campaign) {
        utmParams.push(`utm_campaign=${values.utm.campaign}`)
      }
      if (values.utm.term) {
        utmParams.push(`utm_term=${values.utm.term}`)
      }
      if (values.utm.content) {
        utmParams.push(`utm_content=${values.utm.content}`)
      }

      const utmQueryString = utmParams.join('&')
      const finalUTMString = utmQueryString ? `?${utmQueryString}` : ''

      setUtmTags(finalUTMString)
    } else setUtmTags('-')
  }, [
    values.utm.origin,
    values.utm.media,
    values.utm.campaign,
    values.utm.term,
    values.utm.content,
  ])

  const handleColorChange = (selectedColor: {
    hex: React.SetStateAction<string>
  }) => {
    setColor(selectedColor.hex)
  }

  const handleQRCodeClick = () => {
    setShowPaletteColor(!showPaletteColor)
  }

  const handleChangeUrl = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(e)
    setUrl(e.target.value)
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]

      const imageUrl = URL.createObjectURL(file)
      setPreviewUrl(imageUrl)
      // values.qr_code.logo = file
    }
  }

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <>
      <Form>
        <div className='container-view-url-top'>
          <div className='container-urls'>
            <div className='container-create-url'>
              <Input
                id='url'
                label={`${t('DESTINATION')}`}
                classNameLabel='color-primary'
                type='text'
                value={values.url}
                onChange={handleChangeUrl}
                touched={touched.url}
                classNameInput='input'
                required
                placeholder={`${t('MY_LONG_URL')}`}
                className='col-12'
              />
              <Input
                id='title'
                label={`${t('TITLE')} (${t('OPTIONAL')})`}
                type='text'
                value={values.title}
                onChange={handleChange}
                touched={touched.title}
                classNameInput='input'
                placeholder={`${t('TITLE')}`}
                className='col-12 col-md-6'
                classNameLabel='color-grey-dark'
              />
              <div className='container-check'>
                <input
                  id='utm_checkBox'
                  name='utm_checkBox'
                  type='checkbox'
                  className='input-checkbox-general'
                  checked={values.utm_checkBox}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleCheckboxUTM(e)
                  }
                />

                <label
                  htmlFor='utm'
                  className='label-input-checkbox-general'
                  dangerouslySetInnerHTML={{ __html: `${t('UTM_INFO')}` }}
                />
              </div>
            </div>
          </div>

          {isCheckedUTM && (
            <div className=''>
              <div className='container-utm'>
                <Input
                  id='origin'
                  label={`${t('SOURCE')}`}
                  type='text'
                  value={values.utm.origin}
                  onChange={(e) => handleChange('utm.origin')(e)}
                  touched={touched.utm?.origin}
                  classNameInput='input'
                  placeholder={`${t('EG_GOOGLE_NEWSLETTER')}`}
                  className='col-12 col-md-4'
                  classNameLabel='color-grey-dark'
                />
                <Input
                  id='media'
                  label={`${t('MEDIUM')}`}
                  type='text'
                  value={values.utm.media}
                  onChange={(e) => handleChange('utm.media')(e)}
                  touched={touched.utm?.media}
                  classNameInput='input'
                  placeholder={`${t('EG_BANNER_EMAIL')}`}
                  className='col-12 col-md-4 px-md-4'
                  classNameLabel='color-grey-dark'
                />
                <Input
                  id='campaign'
                  label={`${t('CAMPAIGN')}`}
                  type='text'
                  value={values.utm.campaign}
                  onChange={(e) => handleChange('utm.campaign')(e)}
                  touched={touched.utm?.campaign}
                  classNameInput='input'
                  placeholder={`${t('EG_BLACK_FRIDAY')}`}
                  className='col-12 col-md-4'
                  classNameLabel='color-grey-dark'
                />
              </div>

              <div className='container-utm'>
                <Input
                  id='term'
                  label={`${t('TERM')} (${t('OPTIONAL')})`}
                  classNameLabel='color-grey-dark'
                  type='text'
                  value={values.utm.term}
                  onChange={(e) => handleChange('utm.term')(e)}
                  touched={touched.utm?.term}
                  classNameInput='input'
                  placeholder={`${t('EG_SOMETHING')}`}
                  className='col-12 col-md-4'
                />
                <Input
                  id='content'
                  label={`${t('CONTENT')} (${t('OPTIONAL')})`}
                  classNameLabel='color-grey-dark'
                  type='text'
                  value={values.utm.content}
                  onChange={(e) => handleChange('utm.content')(e)}
                  touched={touched.utm?.content}
                  classNameInput='input'
                  placeholder={`${t('EG_SOMETHING_ELSE')}`}
                  className='col-12 col-md-4 px-md-4'
                />
              </div>

              <div className='container-utm'>
                <span>{`${t('UTM_TAG_PREVIEW')}`}</span>
                <Tooltip
                  title={`${t('UTM_TAG_PREVIEW_QUESTION')}`}
                  placement='top'
                >
                  <FontAwesomeIcon
                    icon={faCircleQuestion}
                    style={{ color: '#ff9f31' }}
                  />
                </Tooltip>
              </div>
              <span>{utmTags}</span>
            </div>
          )}
        </div>
        <div className='container-view-url'>
          <span className='sub-title'>{`${t('WAYS_TO_SHARE')}`}</span>
          <div>
            <img
              src={linkPrimary}
              alt={`${t('URL_SHORT')}`}
              width={20}
              height={20}
              className='margin-right-1'
            />
            <span className='sub-title-primary'>{`${t('URL_SHORT')}`}</span>
          </div>

          <div className='container-urls'>
            <div className='container-create-url-domain'>
              <Select
                disabled={!userInfo.permissions.custom_domain}
                optionSelect={false}
                options={domainData}
                id='domain'
                label={`${t('DOMAIN')}`}
                value={values.domain}
                classNameSelect='customized-select-form'
                onChange={handleChange}
                className='col-12'
              />
              {sizeWidth > 767 && (
                <div className='container-link-separator'>
                  <span className='link-separator'>/</span>
                </div>
              )}

              <Input
                disabled={!customDomain}
                id='custom_code'
                label={`${t('MY_CUSTOM_URL')} (${t('OPTIONAL')})`}
                classNameLabel='color-grey-dark'
                type='text'
                value={values.custom_code}
                onChange={handleChange}
                touched={touched.custom_code}
                classNameInput='input'
                placeholder={`${t('MY_CUSTOM_URL')}`}
                className='col-12 col-md-6'
              />
            </div>
            <div className='container-check-qr'>
              <img
                src={qrCodesActiveSecondary}
                alt={`${t('QR_CODE')}`}
                width={20}
                height={20}
                className='margin-right-1'
              />

              <span className='sub-title-secondary'>{`${t('QR_CODE')}`}</span>
              <span>{` (${t('OPTIONAL')})`}</span>
            </div>
            <div className='container-check'>
              <input
                id='qr_code_checkBox'
                name='qr_code_checkBox'
                type='checkbox'
                className='input-checkbox-general'
                checked={isCheckedQR}
                onChange={handleCheckboxQR}
              />

              <label
                htmlFor='qrCode'
                className='label-input-checkbox-general'
                dangerouslySetInnerHTML={{ __html: `${t('QR_CODE_INFO')}` }}
              />
            </div>
          </div>
          {isCheckedQR && (
            <>
              <div className=''>
                <div className='container-qr'>
                  <div className='column-qr'>
                    <div className='d-flex position-relative'>
                      <button
                        type='button'
                        className='button-select-color'
                        style={{ backgroundColor: color }}
                        onClick={handleQRCodeClick}
                      ></button>
                      <Input
                        id='color'
                        label={`${t('QR_CODE_COLOR')}`}
                        classNameLabel='color-black'
                        type='text'
                        value={values.qr_code.color}
                        onChange={handleChange}
                        touched={touched.qr_code?.color}
                        classNameInput='input'
                        className='col-12 col-md-6'
                        customClass='input-select-qr-code'
                      />
                    </div>

                    {showPaletteColor && (
                      <ChromePicker
                        color={color}
                        onChange={handleColorChange}
                        className='position-absolute'
                      />
                    )}
                    <span className='small'>{`${t(
                      'QR_WARNING_CREATION'
                    )}`}</span>
                    {/* <div className='d-grid'>
                      <button
                        type='button'
                        className='button-sixth my-4'
                        onClick={handleButtonClick}
                      >{`${t('UPLOAD_YOUR_LOGO')}`}</button>

                      <input
                        type='file'
                        accept='image/*'
                        ref={fileInputRef}
                        className='input-file'
                        onChange={handleFileChange}
                        id='qrLogo'
                        name='qrLogo'
                      />
                      <span className='small'>{`${t(
                        'QR_CODE_LOGO_INFO'
                      )}`}</span>
                    </div> */}
                  </div>
                  <div className='column-qr'>
                    {url && (
                      <>
                        <div className='d-grid'>
                          <span>{`${t('PREVIEW')}`}</span>
                          <span className='small'>{`${t(
                            'PREVIEW_QR_CODE'
                          )}`}</span>
                        </div>

                        <div className='container-qr-code-center'>
                          <div className='container-qr-code'>
                            <QRCode
                              value={shortUrlGenerated}
                              fgColor={color}
                              size={100}
                              bgColor='transparent'
                              level='H'
                              imageSettings={{
                                src: !previewUrl ? '' : previewUrl,
                                height: 35,
                                width: 35,
                                excavate: true,
                              }}
                            />
                          </div>
                        </div>

                        <span className='small'>{`${t(
                          'QR_CODE_CHARACTERISTICS'
                        )}`}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className='container-butttons-general-right'>
          <button className='button-third margin-right-2' type='button'>
            {`${t('CANCEL')}`}
          </button>
          <button className='button-first' type='submit'>
            {`${t('CREATE')}`}
          </button>
        </div>
      </Form>
    </>
  )
}

const CreateNewForm = withFormik<CreateNewFormProps, CreateNewFormValues>({
  mapPropsToValues: (props: {
    url: any
    domain: any
    utm_checkBox: any
    qr_code_checkBox: any
    qr_code: any
    utm: any
  }) => {
    const { url, domain, utm_checkBox, qr_code_checkBox, qr_code, utm } = props
    const initialValues = {
      url,
      domain,
      title: '',
      custom_code: '',
      tags: [],
      has_qr: qr_code_checkBox,
      utm: {
        origin: '',
        media: '',
        campaign: utm.campaign || '',
        term: utm.term || '',
        content: utm.content || '',
      },
      qr_code: {
        color: qr_code.color || '',
        logo: qr_code.logo || '',
      },
      qr_code_checkBox,
      utm_checkBox,
    }

    return initialValues
  },

  handleSubmit(values: CreateNewFormValues, { props }: any) {
    const hexRegex = /^#([A-Fa-f0-9]{3}){1,2}$/
    const { qr_code_checkBox, qr_code } = values

    if (qr_code_checkBox) {
      if (!qr_code.color || !hexRegex.test(qr_code.color)) {
        toast(`${t('ERROR_HEXADECIMAL_COLOR')}`)
        return
      }
    }

    props.onSubmit(values)
  },
})(InnerForm)
export default CreateNewForm
